// @ts-check
// Responsável pela conversa: menu de membros da equipe com busca, usado no seletor do cabeçalho
// e no filtro "Responsável" da lista.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { currentWorkspaceMemberState } from '../../data/metadata-store.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { listAssignableThreadMembers } from '../../data/conversations.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { Avatar } from '../../ui/avatar.js';
import { Icon } from '../../ui/icon.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSearchInput, DropdownMenuSeparator, MenuItem, useKeyboardSelection } from '../../ui/dropdown.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {{ id: string, text: string, icon: string }} FixedMenuItem
 * @typedef {{ id: string, text: string, icon?: string, memberId?: string }} MemberMenuItem
 */

/** Valor do filtro de responsável que pede as conversas sem responsável (contrato da lista). */
export const NO_ASSIGNEE = 'NONE';
export const ALL_ASSIGNEES = 'ALL';
export const MY_CONVERSATIONS = 'MINE';
// equipes pequenas cabem inteiras no menu; acima disso a lista rola
const MENU_ITEMS_WITHOUT_SCROLL = 8;

/** Carrega a lista de membros uma vez por sessão e devolve o estado atual. */
export function useWorkspaceMembers() {
  useEffect(() => {
    loadWorkspaceMembers();
  }, []);
  return workspaceMembersState.value ?? [];
}

/**
 * @param {string | null | undefined} memberId
 * @param {import('../../data/workspace-members.js').WorkspaceMember[]} members
 */
export function findMemberName(memberId, members) {
  const member = members.find((candidate) => candidate.id === memberId);
  return member ? formatFullName(member.name) : '';
}

/**
 * @param {{ name: string, seed: string, size?: 'sm' | 'md' | 'lg' }} props
 */
export function MemberAvatar({ name, seed, size = 'md' }) {
  return html`<${Avatar} placeholder=${name} seed=${seed} size=${size} type="rounded" />`;
}

/**
 * Lista com busca: itens fixos no topo (ex.: "Sem responsável") e um item por membro.
 * @param {{ fixedItems: FixedMenuItem[], selectedId: string | null, onSelect: (id: string) => void, allowedMemberIds?: Set<string> | null }} props
 *   allowedMemberIds: só esses membros (responsáveis possíveis da conversa); nulo mostra todos
 */
export function WorkspaceMemberMenu({ fixedItems, selectedId, onSelect, allowedMemberIds = null }) {
  const members = useWorkspaceMembers().filter((member) => !allowedMemberIds || allowedMemberIds.has(member.id));
  const [searchText, setSearchText] = useState('');
  const query = normalizeSearchText(searchText.trim());
  /** @type {MemberMenuItem[]} */
  const items = [
    ...fixedItems.filter((item) => !query || normalizeSearchText(item.text).includes(query)),
    ...members
      .map((member) => ({ id: member.id, text: formatFullName(member.name), memberId: member.id }))
      .filter((item) => !query || normalizeSearchText(item.text).includes(query)),
  ];
  const { focusedIndex, setFocusedIndex, handleKeyDown } = useKeyboardSelection(items.length, (index) => onSelect(items[index].id));
  return html`
    <${DropdownMenuSearchInput}
      value=${searchText}
      placeholder=${t('conversations.quickFilters.searchMember')}
      onChange=${(value) => {
        setSearchText(value);
        setFocusedIndex(-1);
      }}
      onKeyDown=${handleKeyDown}
    />
    <${DropdownMenuSeparator} />
    <${DropdownMenuItems} hasMaxHeight=${items.length > MENU_ITEMS_WITHOUT_SCROLL}>
      ${items.length === 0
        ? html`<div class="menu-item-empty">${t('common.noResults')}</div>`
        : items.map((item, index) => html`<${MenuItem}
            key=${item.id}
            text=${item.text}
            icon=${item.icon}
            leftComponent=${item.memberId ? html`<${MemberAvatar} name=${item.text} seed=${item.memberId} />` : undefined}
            selected=${item.id === selectedId}
            focused=${index === focusedIndex}
            onClick=${() => onSelect(item.id)}
          />`)}
    <//>
  `;
}

/**
 * Seletor do cabeçalho: avatar e nome do responsável ou "Sem responsável". A lista mostra só quem lê a conversa pelas
 * etapas do papel; sem a resposta da rota (modo demonstração antigo), mostra todos e o servidor decide.
 * @param {{ threadId?: string, assignee: import('../../data/conversations.js').ThreadAssignee | null | undefined, onAssign: (workspaceMemberId: string | null) => void }} props
 */
export function AssigneePicker({ threadId, assignee, onAssign }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [allowedMemberIds, setAllowedMemberIds] = useState(/** @type {Set<string> | null} */ (null));
  useEffect(() => {
    if (!isOpen || !threadId) return undefined;
    let isCancelled = false;
    listAssignableThreadMembers(threadId)
      .then((members) => {
        if (!isCancelled) setAllowedMemberIds(new Set(members.map((member) => member.id)));
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [isOpen, threadId]);
  const members = useWorkspaceMembers();
  const name = assignee ? assignee.name || findMemberName(assignee.workspaceMemberId, members) : '';
  return html`<span class="conversation-assignee">
    <button
      type="button"
      ref=${anchorRef}
      class="conversation-assignee-button"
      data-empty=${assignee ? undefined : 'true'}
      aria-haspopup="listbox"
      aria-expanded=${isOpen ? 'true' : 'false'}
      title=${t('conversations.assignee.change')}
      onClick=${() => setIsOpen(!isOpen)}
    >
      ${assignee
        ? html`<${MemberAvatar} name=${name} seed=${assignee.workspaceMemberId} />`
        : html`<${Icon} name="IconUserCircle" size=${16} />`}
      <span class="conversation-assignee-name">${assignee ? name : t('conversations.assignee.none')}</span>
      <${Icon} name="IconChevronDown" size=${14} className="conversation-assignee-chevron" />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${240}>
      <${WorkspaceMemberMenu}
        fixedItems=${[{ id: NO_ASSIGNEE, text: t('conversations.assignee.none'), icon: 'IconUserX' }]}
        allowedMemberIds=${allowedMemberIds}
        selectedId=${assignee?.workspaceMemberId ?? NO_ASSIGNEE}
        onSelect=${(id) => {
          setIsOpen(false);
          const nextId = id === NO_ASSIGNEE ? null : id;
          if (nextId !== (assignee?.workspaceMemberId ?? null)) onAssign(nextId);
        }}
      />
    <//>
  </span>`;
}

/**
 * Filtro "Responsável" da lista: Todos, Minhas conversas, Sem responsável ou um membro.
 * @param {{ value: string, onChange: (value: string) => void }} props
 */
export function AssigneeFilterButton({ value, onChange }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const members = useWorkspaceMembers();
  const isActive = value !== ALL_ASSIGNEES;
  const currentMemberId = currentWorkspaceMemberState.value?.id ?? null;
  // sem filtro o botão diz o que filtra; com filtro mostra só o valor, para caber ao lado dos chips
  const buttonLabel =
    value === ALL_ASSIGNEES
      ? t('conversations.quickFilters.assignee')
      : value === MY_CONVERSATIONS
        ? t('conversations.quickFilters.assigneeMine')
        : value === NO_ASSIGNEE
          ? t('conversations.quickFilters.assigneeNone')
          : findMemberName(value, members) || t('conversations.quickFilters.assignee');
  /** @type {FixedMenuItem[]} */
  const fixedItems = [
    { id: ALL_ASSIGNEES, text: t('conversations.quickFilters.assigneeAll'), icon: 'IconUsers' },
    ...(currentMemberId ? [{ id: MY_CONVERSATIONS, text: t('conversations.quickFilters.assigneeMine'), icon: 'IconUserCheck' }] : []),
    { id: NO_ASSIGNEE, text: t('conversations.quickFilters.assigneeNone'), icon: 'IconUserX' },
  ];
  return html`<span class="thread-quick-filter-anchor">
    <button
      type="button"
      ref=${anchorRef}
      class="thread-quick-filter"
      data-active=${isActive ? 'true' : undefined}
      aria-haspopup="listbox"
      aria-expanded=${isOpen ? 'true' : 'false'}
      title=${isActive ? `${t('conversations.quickFilters.assignee')}: ${buttonLabel}` : buttonLabel}
      onClick=${() => setIsOpen(!isOpen)}
    >
      <${Icon} name="IconUserCircle" size=${14} />
      <span class="thread-quick-filter-value">${buttonLabel}</span>
      <${Icon} name="IconChevronDown" size=${12} />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-start" width=${240}>
      <${WorkspaceMemberMenu}
        fixedItems=${fixedItems}
        selectedId=${value === MY_CONVERSATIONS && !currentMemberId ? ALL_ASSIGNEES : value}
        onSelect=${(id) => {
          setIsOpen(false);
          onChange(id);
        }}
      />
    <//>
  </span>`;
}
